/**
 * ================================================================
 * NOTIFICATION API - Pure Service Layer (No React)
 * ================================================================
 *
 * Handles Capacitor Push Notifications API
 * - Channel creation (Android heads-up)
 * - Permission handling (Android 13+)
 * - Token registration
 *
 * NO REACT HOOKS - Pure TypeScript singleton
 * Event handling done in useNotificationListener hook
 *
 * @author MeBi Team
 * @version 3.0.0
 */

import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import apiClient from "@/lib/apiClient";

class NotificationApi {
  private static instance: NotificationApi;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): NotificationApi {
    if (!NotificationApi.instance) {
      NotificationApi.instance = new NotificationApi();
    }
    return NotificationApi.instance;
  }

  /**
   * Initialize push notifications (Capacitor only, no React)
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (!Capacitor.isNativePlatform()) {
      console.log("Push notifications not supported on web");
      return;
    }

    try {
      await this.setupNotificationChannel();
      const permissionGranted = await this.checkAndRequestPermissions();
      if (!permissionGranted) {
        this.isInitialized = true;
        console.warn(
          "⚠️ Notification initialization completed without permission",
        );
        return;
      }

      await this.registerPushNotifications();

      this.isInitialized = true;
      console.log("✅ NotificationApi initialized");
    } catch (error) {
      console.error("❌ NotificationApi init failed:", error);
    }
  }

  /**
   * Create Android notification channel (High importance for heads-up)
   */
  private async setupNotificationChannel() {
    try {
      await PushNotifications.createChannel({
        id: "mebi_notifications",
        name: "MeBi Notifications",
        description: "Tournament updates and organizer approvals",
        importance: 5, // IMPORTANCE_HIGH
        visibility: 1,
        vibration: true,
        lights: true,
        lightColor: "#FF0000",
        sound: "default",
      });
      console.log("✅ Notification channel created");
    } catch (error) {
      console.error("Failed to create notification channel:", error);
    }
  }

  /**
   * Handle Android 13+ runtime permissions
   */
  private async checkAndRequestPermissions(): Promise<boolean> {
    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (
        permStatus.receive === "prompt" ||
        permStatus.receive === "prompt-with-rationale"
      ) {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== "granted") {
        console.warn("⚠️ Push notification permission denied");
        return false;
      }

      console.log("✅ Push notification permission granted");
      return true;
    } catch (error) {
      console.error("Permission check failed:", error);
      return false;
    }
  }

  /**
   * Register device for push notifications
   */
  private async registerPushNotifications() {
    await PushNotifications.register();
  }

  /**
   * Send FCM token to backend
   */
  public async sendTokenToBackend(token: string) {
    try {
      const platform = Capacitor.getPlatform();
      console.log("Sending FCM token to backend");

      await apiClient.post("/notifications/register-token", {
        token: token,
        platform: platform,
      });

      console.log("✅ FCM token registered");
    } catch (error) {
      console.error("❌ Failed to send FCM token:", error);
    }
  }

  /**
   * Retry token registration (called after login)
   */
  public async retryTokenRegistration(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await PushNotifications.register();
      console.log("🔄 Retrying FCM token registration");
    } catch (error) {
      console.warn("Could not retry FCM registration:", error);
    }
  }

  /**
   * Cleanup listeners
   */
  public removeAllListeners() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    PushNotifications.removeAllListeners();
  }
}

export const notificationApi = NotificationApi.getInstance();
export default notificationApi;
